import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import type { RenderTask } from 'pdfjs-dist';
import { IconComponent } from '../ui/icon/icon.component';
import { SpinnerComponent } from '../ui/spinner/spinner.component';
import {
  DEFAULT_PDF_VIEWER_CONFIG,
  PDF_VIEWER_CONFIG,
  PdfViewerConfig,
} from './pdf-viewer.config';
import {
  PdfDocument,
  PdfFitMode,
  PdfRotation,
  PdfSearchSummary,
  PdfSource,
  PdfTheme,
  PdfViewerError,
} from './models/pdf-viewer.model';
import { PdfDownloadService } from './services/pdf-download.service';
import { PdfPrintService } from './services/pdf-print.service';
import { PdfSearchService } from './services/pdf-search.service';
import { PdfStateService } from './services/pdf-state.service';
import { PdfViewerService } from './services/pdf-viewer.service';
import { PdfZoomService } from './services/pdf-zoom.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

/** Layout box for a single page placeholder in the virtual list. */
interface PageLayout {
  number: number;
  width: number;
  height: number;
}

/**
 * Reusable, enterprise-grade PDF viewer built on PDF.js with Kendo-consistent chrome.
 *
 * Renders 1000+ page documents via a virtualised page list: every page gets a lightweight sized
 * placeholder, and an `IntersectionObserver` renders only the canvases near the viewport while an
 * LRU cap releases off-screen canvases to bound memory. All lifecycle-owning services
 * (`PdfViewerService`, `PdfStateService`, `PdfSearchService`) are provided per-instance so several
 * viewers can coexist and each disposes its own document on destroy.
 */
@Component({
  selector: 'app-pdf-viewer',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, SpinnerComponent, NgbTooltipModule],
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
  providers: [PdfViewerService, PdfStateService, PdfSearchService],
  host: {
    class: 'pdf-viewer',
    '[class.pdf-viewer--dark]': "state.theme() === 'dark'",
    '[class.pdf-viewer--fullscreen]': 'state.fullscreen()',
    '[attr.tabindex]': '0',
    '[attr.role]': "'region'",
    '[attr.aria-label]': "'PDF document viewer'",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly viewer = inject(PdfViewerService);
  private readonly zoomSvc = inject(PdfZoomService);
  private readonly download = inject(PdfDownloadService);
  private readonly printer = inject(PdfPrintService);
  private readonly search = inject(PdfSearchService);

  /** Public reactive store — read by the template. */
  readonly state = inject(PdfStateService);

  private readonly config: PdfViewerConfig = {
    ...DEFAULT_PDF_VIEWER_CONFIG,
    ...(inject(PDF_VIEWER_CONFIG, { optional: true }) ?? {}),
  };

  // ---- Inputs (per-instance overrides of the global config) --------------------------------
  @Input() src!: PdfSource;
  @Input() fileName?: string;
  @Input() readOnly = true;
  @Input() showToolbar = this.config.showToolbar;
  @Input() showSidebar = this.config.showSidebar;
  @Input() showThumbnails = this.config.showThumbnails;
  @Input() showSearch = this.config.showSearch;
  @Input() showDownload = this.config.showDownload;
  @Input() showPrint = this.config.showPrint;
  @Input() showRotate = this.config.showRotate;
  @Input() showZoom = this.config.showZoom;
  @Input() showFullscreen = this.config.showFullscreen;
  @Input() showTheme = this.config.showTheme;
  @Input() fitMode: PdfFitMode = this.config.fitMode;
  @Input() initialZoom = this.config.defaultZoom;
  @Input() minZoom = this.config.minZoom;
  @Input() maxZoom = this.config.maxZoom;
  @Input() theme: PdfTheme = this.config.theme;
  @Input() enableKeyboardNavigation = this.config.enableKeyboardNavigation;
  @Input() enableContextMenu = this.config.enableContextMenu;
  @Input() watermark?: string;
  @Input() password?: string;
  /** Optional custom loading UI. */
  @Input() loadingTemplate?: TemplateRef<unknown>;
  /** Optional custom error UI; receives `{ $implicit: error, retry }`. */
  @Input() errorTemplate?: TemplateRef<{ $implicit: PdfViewerError; retry: () => void }>;
  /** Emitted so the toolbar "Back" button can be wired by the host; hidden when unbound. */
  @Input() showBack = false;

  // ---- Outputs ------------------------------------------------------------------------------
  @Output() documentLoaded = new EventEmitter<PdfDocument>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() zoomChanged = new EventEmitter<number>();
  @Output() rotated = new EventEmitter<PdfRotation>();
  @Output() downloaded = new EventEmitter<void>();
  @Output() printed = new EventEmitter<void>();
  /** Named `errored` (not `error`) to avoid colliding with the native DOM `error` event. */
  @Output() errored = new EventEmitter<PdfViewerError>();
  @Output() searchCompleted = new EventEmitter<PdfSearchSummary>();
  @Output() fullscreenChanged = new EventEmitter<boolean>();
  @Output() back = new EventEmitter<void>();

  @ViewChild('scroller') private readonly scroller?: ElementRef<HTMLElement>;
  @ViewChildren('pageEl') private readonly pageEls?: QueryList<ElementRef<HTMLElement>>;

  readonly pageLayouts = signal<PageLayout[]>([]);
  readonly pageInput = signal('1');
  readonly searchInput = signal('');

  private observer?: IntersectionObserver;
  private resizeObserver?: ResizeObserver;
  private resizeRaf = 0;
  private readonly renderTasks = new Map<number, RenderTask>();
  private readonly rendered = new Set<number>();
  private readonly lruOrder: number[] = [];
  private readonly visible = new Set<number>();
  private readonly ratios = new Map<number, number>();
  private readonly pageSizes = new Map<number, { w: number; h: number }>();
  // Bumped on every teardown/rerender so an in-flight render started before its task was tracked
  // can detect it is stale and cancel itself instead of painting over the fresh render.
  private renderGeneration = 0;
  private baseSize = { w: 612, h: 792 };
  private manualZoom = false;
  private viewReady = false;

  // ---- Lifecycle ----------------------------------------------------------------------------
  ngOnInit(): void {
    this.viewer.configureWorker(this.config.workerSrc);
    this.state.setTheme(this.theme);
    this.state.setFitMode(this.fitMode);
    this.state.setZoom(this.initialZoom);
    this.state.setSidebarOpen(this.showSidebar);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['theme'] && !changes['theme'].firstChange) {
      this.state.setTheme(this.theme);
    }
    if (changes['fitMode'] && !changes['fitMode'].firstChange) {
      this.setFit(this.fitMode);
    }
    if (changes['src'] && this.src != null && this.src !== '') {
      if (this.viewReady) {
        void this.load();
      }
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.setupResizeObserver();
    this.pageEls?.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.observePages());
    if (this.src != null && this.src !== '') {
      void this.load();
    }
  }

  ngOnDestroy(): void {
    this.teardownRendering();
    this.resizeObserver?.disconnect();
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }
    void this.viewer.destroy();
  }

  // ---- Loading ------------------------------------------------------------------------------
  async load(): Promise<void> {
    this.teardownRendering();
    this.state.reset();
    this.state.beginLoading();
    this.pageLayouts.set([]);

    try {
      const doc = await this.viewer.load(this.src, {
        fileName: this.fileName,
        password: this.password,
        onProgress: (fraction) => this.zone.run(() => this.state.setProgress(fraction)),
      });

      const firstSize = await this.viewer.getPageSize(1);
      this.baseSize = { w: firstSize.width, h: firstSize.height };
      this.pageSizes.set(1, this.baseSize);

      this.zone.run(() => {
        this.state.setDocument(doc);
        this.manualZoom = false;
        this.applyFit();
        this.buildLayouts();
        this.pageInput.set('1');
        this.documentLoaded.emit(doc);
        // Attach the observer once the placeholders exist in the DOM.
        queueMicrotask(() => this.observePages());
      });
    } catch (cause) {
      const error = this.asError(cause);
      this.zone.run(() => {
        this.state.setError(error);
        this.errored.emit(error);
      });
    }
  }

  /** Arrow field so it can be passed by reference to a custom error template. */
  readonly retry = (): void => {
    void this.load();
  };

  // ---- Virtualised rendering ---------------------------------------------------------------
  private observePages(): void {
    if (!this.scroller) {
      return;
    }
    this.observer?.disconnect();
    // Create outside Angular so scroll-driven intersection callbacks don't trigger change detection;
    // the few state writes that must (current page) re-enter the zone explicitly.
    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
        root: this.scroller?.nativeElement ?? null,
        // Pre-render roughly two viewport heights ahead/behind for smooth scrolling.
        rootMargin: '150% 0px 150% 0px',
        threshold: [0, 0.01, 0.25, 0.5, 0.75, 1],
      });
      this.pageEls?.forEach((ref) => this.observer?.observe(ref.nativeElement));
    });
  }

  private onIntersect(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const page = Number((entry.target as HTMLElement).dataset['page']);
      if (!page) {
        continue;
      }
      if (entry.isIntersecting) {
        this.visible.add(page);
        this.ratios.set(page, entry.intersectionRatio);
        void this.renderPage(page);
      } else {
        this.visible.delete(page);
        this.ratios.delete(page);
      }
    }
    this.enforceLru();
    this.syncCurrentPage();
  }

  private async renderPage(page: number): Promise<void> {
    if (this.rendered.has(page) || this.renderTasks.has(page)) {
      return;
    }
    const element = this.pageElement(page);
    const canvas = element?.querySelector('canvas');
    if (!element || !(canvas instanceof HTMLCanvasElement)) {
      return;
    }

    const generation = this.renderGeneration;
    const scale = this.zoomSvc.toScale(this.state.zoom());
    const rotation = this.state.rotation();
    try {
      const task = await this.viewer.renderPage(page, canvas, scale, rotation, this.devicePixelRatio());
      // A zoom/rotate/reload happened while renderPage was awaiting getPage: this task is not yet
      // tracked, so rerenderAll()/teardown could not have cancelled it. Drop it before it paints.
      if (generation !== this.renderGeneration) {
        task.cancel();
        return;
      }
      this.renderTasks.set(page, task);
      await task.promise;
      this.renderTasks.delete(page);
      this.rendered.add(page);
      this.touchLru(page);
      element.classList.add('pdf-page--rendered');

      if (!this.pageSizes.has(page)) {
        const size = await this.viewer.getPageSize(page);
        this.pageSizes.set(page, { w: size.width, h: size.height });
        this.zone.run(() => this.buildLayouts());
      }
      this.enforceLru();
    } catch (cause) {
      this.renderTasks.delete(page);
      if (!this.isCancellation(cause)) {
        const error = this.asError(cause);
        this.zone.run(() => this.errored.emit(error));
      }
    }
  }

  private releasePage(page: number): void {
    this.renderTasks.get(page)?.cancel();
    this.renderTasks.delete(page);
    this.rendered.delete(page);
    const index = this.lruOrder.indexOf(page);
    if (index !== -1) {
      this.lruOrder.splice(index, 1);
    }
    const element = this.pageElement(page);
    const canvas = element?.querySelector('canvas');
    if (canvas instanceof HTMLCanvasElement) {
      canvas.width = 0;
      canvas.height = 0;
      canvas.removeAttribute('style');
    }
    element?.classList.remove('pdf-page--rendered');
  }

  private touchLru(page: number): void {
    const index = this.lruOrder.indexOf(page);
    if (index !== -1) {
      this.lruOrder.splice(index, 1);
    }
    this.lruOrder.push(page);
  }

  private enforceLru(): void {
    const max = Math.max(3, this.config.maxRenderedPages);
    let cursor = 0;
    while (this.rendered.size > max && cursor < this.lruOrder.length) {
      const candidate = this.lruOrder[cursor];
      if (this.visible.has(candidate)) {
        cursor++;
        continue;
      }
      this.releasePage(candidate);
    }
  }

  private rerenderAll(): void {
    this.renderGeneration++;
    for (const page of [...this.rendered, ...this.renderTasks.keys()]) {
      this.releasePage(page);
    }
    this.rendered.clear();
    this.renderTasks.clear();
    this.lruOrder.length = 0;
    for (const page of this.visible) {
      void this.renderPage(page);
    }
  }

  private syncCurrentPage(): void {
    let bestPage = this.state.currentPage();
    let bestRatio = -1;
    for (const [page, ratio] of this.ratios) {
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestPage = page;
      }
    }
    if (bestPage !== this.state.currentPage()) {
      this.zone.run(() => {
        this.state.setCurrentPage(bestPage);
        this.pageInput.set(String(bestPage));
        this.pageChanged.emit(bestPage);
      });
    }
  }

  // ---- Layout / zoom ------------------------------------------------------------------------
  private buildLayouts(): void {
    const count = this.state.pageCount();
    const scale = this.zoomSvc.toScale(this.state.zoom());
    const rotation = this.state.rotation();
    const rotated = rotation === 90 || rotation === 270;
    const layouts: PageLayout[] = [];
    for (let n = 1; n <= count; n++) {
      const size = this.pageSizes.get(n) ?? this.baseSize;
      const w = rotated ? size.h : size.w;
      const h = rotated ? size.w : size.h;
      layouts.push({ number: n, width: Math.round(w * scale), height: Math.round(h * scale) });
    }
    this.pageLayouts.set(layouts);
  }

  private applyFit(): void {
    if (this.manualZoom) {
      return;
    }
    const viewport = this.viewportSize();
    const zoom = this.zoomSvc.fitToZoom(
      this.state.fitMode(),
      { width: this.baseSize.w, height: this.baseSize.h },
      viewport,
      this.state.rotation(),
      this.minZoom,
      this.maxZoom
    );
    this.state.setZoom(zoom);
    this.zoomChanged.emit(zoom);
  }

  private viewportSize(): { width: number; height: number } {
    const element = this.scroller?.nativeElement;
    // Keep `||` (not `??`): a clientWidth/clientHeight of 0 (element not yet laid out or hidden)
    // must also fall back to the default viewport, so falsy 0 is intentionally treated as absent.
    return {
      width: element?.clientWidth || 800, // NOSONAR S6606 — intentional `||` (see comment above)
      height: element?.clientHeight || 600, // NOSONAR S6606 — intentional `||` (see comment above)
    };
  }

  // ---- Toolbar actions ----------------------------------------------------------------------
  zoomIn(): void {
    this.zoomTo(this.zoomSvc.zoomIn(this.state.zoom(), this.config.zoomStep, this.maxZoom));
  }

  zoomOut(): void {
    this.zoomTo(this.zoomSvc.zoomOut(this.state.zoom(), this.config.zoomStep, this.minZoom));
  }

  zoomTo(zoom: number): void {
    const clamped = this.zoomSvc.clamp(zoom, this.minZoom, this.maxZoom);
    if (clamped === this.state.zoom()) {
      return;
    }
    this.manualZoom = true;
    this.state.setZoom(clamped);
    this.buildLayouts();
    this.rerenderAll();
    this.zoomChanged.emit(clamped);
  }

  setFit(mode: PdfFitMode): void {
    this.state.setFitMode(mode);
    this.manualZoom = false;
    this.applyFit();
    this.buildLayouts();
    this.rerenderAll();
  }

  rotate(direction: 'left' | 'right'): void {
    const rotation = this.state.rotate(direction);
    if (!this.manualZoom) {
      this.applyFit();
    }
    this.buildLayouts();
    this.rerenderAll();
    this.rotated.emit(rotation);
  }

  goToPage(page: number): void {
    const count = this.state.pageCount();
    if (count === 0) {
      return;
    }
    const target = Math.min(count, Math.max(1, Math.round(page)));
    this.state.setCurrentPage(target);
    this.pageInput.set(String(target));
    this.scrollToPage(target);
  }

  previousPage(): void {
    this.goToPage(this.state.currentPage() - 1);
  }

  nextPage(): void {
    this.goToPage(this.state.currentPage() + 1);
  }

  onPageInputCommit(value: string): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      this.goToPage(parsed);
    } else {
      this.pageInput.set(String(this.state.currentPage()));
    }
  }

  private scrollToPage(page: number): void {
    const element = this.pageElement(page);
    const container = this.scroller?.nativeElement;
    if (!element || !container) {
      return;
    }
    const top =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    container.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
  }

  onDownload(): void {
    const name = this.state.document()?.fileName ?? this.fileName ?? 'document.pdf';
    const data = this.viewer.getRawData();
    if (data) {
      this.download.download(data, name);
    } else if (typeof this.src === 'string') {
      this.download
        .downloadFromUrl(this.src, name)
        .catch((cause) => this.errored.emit(this.asError(cause)));
    }
    this.downloaded.emit();
  }

  onPrint(): void {
    const data = this.viewer.getRawData();
    let done: Promise<void>;
    if (data) {
      done = this.printer.print(data);
    } else if (typeof this.src === 'string') {
      done = this.printer.printUrl(this.src);
    } else {
      done = Promise.reject(new Error('Nothing to print.'));
    }
    done.then(() => this.printed.emit()).catch((cause) => this.errored.emit(this.asError(cause)));
  }

  toggleFullscreen(): void {
    const root = this.host.nativeElement;
    if (!document.fullscreenElement) {
      void root.requestFullscreen?.().catch(() => undefined);
    } else {
      void document.exitFullscreen?.();
    }
  }

  toggleTheme(): void {
    this.state.toggleTheme();
  }

  toggleSidebar(): void {
    this.state.toggleSidebar();
  }

  toggleSearch(): void {
    const open = !this.state.searchOpen();
    this.state.setSearchOpen(open);
    if (!open) {
      this.state.setSearchSummary(null);
    }
  }

  onBack(): void {
    this.back.emit();
  }

  async runSearch(query: string): Promise<void> {
    const summary = await this.search.search(query);
    this.zone.run(() => {
      this.state.setSearchSummary(summary);
      this.searchCompleted.emit(summary);
    });
    if (summary.totalMatches > 0) {
      this.goToMatch(0);
    }
  }

  nextMatch(): void {
    this.goToMatch(this.state.activeMatchIndex() + 1);
  }

  previousMatch(): void {
    this.goToMatch(this.state.activeMatchIndex() - 1);
  }

  private goToMatch(index: number): void {
    const results = this.state.searchSummary()?.results ?? [];
    if (results.length === 0) {
      return;
    }
    const wrapped = ((index % results.length) + results.length) % results.length;
    this.state.setActiveMatchIndex(wrapped);
    this.goToPage(results[wrapped].pageNumber);
  }

  // ---- Host events --------------------------------------------------------------------------
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.enableKeyboardNavigation || !this.state.isLoaded()) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
      case 'PageDown':
        this.nextPage();
        break;
      case 'ArrowLeft':
      case 'PageUp':
        this.previousPage();
        break;
      case ' ':
        this.nextPage();
        break;
      case 'Home':
        this.goToPage(1);
        break;
      case 'End':
        this.goToPage(this.state.pageCount());
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case 'f':
      case 'F':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          this.state.setSearchOpen(true);
        }
        return;
      default:
        return;
    }
    event.preventDefault();
  }

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent): void {
    if (!this.enableContextMenu) {
      event.preventDefault();
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const active = document.fullscreenElement === this.host.nativeElement;
    this.state.setFullscreen(active);
    this.fullscreenChanged.emit(active);
    if (!this.manualZoom) {
      queueMicrotask(() => {
        this.applyFit();
        this.buildLayouts();
        this.rerenderAll();
      });
    }
  }

  // ---- Helpers ------------------------------------------------------------------------------
  trackByPage(_index: number, layout: PageLayout): number {
    return layout.number;
  }

  private setupResizeObserver(): void {
    const element = this.scroller?.nativeElement;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(element);
    });
  }

  private onResize(): void {
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }
    this.resizeRaf = requestAnimationFrame(() => this.onResizeFrame());
  }

  private onResizeFrame(): void {
    if (!this.manualZoom && this.state.isLoaded()) {
      this.zone.run(() => {
        this.applyFit();
        this.buildLayouts();
        this.rerenderAll();
      });
    }
  }

  private teardownRendering(): void {
    this.renderGeneration++;
    this.observer?.disconnect();
    this.observer = undefined;
    for (const task of this.renderTasks.values()) {
      task.cancel();
    }
    this.renderTasks.clear();
    this.rendered.clear();
    this.lruOrder.length = 0;
    this.visible.clear();
    this.ratios.clear();
    this.pageSizes.clear();
  }

  private pageElement(page: number): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>(`.pdf-page[data-page="${page}"]`);
  }

  private devicePixelRatio(): number {
    return Math.min(this.config.maxDevicePixelRatio, window.devicePixelRatio || 1);
  }

  private isCancellation(cause: unknown): boolean {
    return (cause as { name?: string })?.name === 'RenderingCancelledException';
  }

  private asError(cause: unknown): PdfViewerError {
    if (
      typeof cause === 'object' &&
      cause !== null &&
      'code' in cause &&
      'message' in cause
    ) {
      return cause as PdfViewerError;
    }
    return {
      code: 'unknown',
      message: (cause as { message?: string })?.message ?? 'An unexpected error occurred.',
      cause,
    };
  }
}
